import { useState, useRef } from "react";

// Custom hook for form validation and error handling
export const useFormValidation = (initialValues, validationRules) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);
  const rulesRef = useRef(validationRules);

  // Handle input change - no validation, just update values
  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Handle input blur - no validation, just mark as touched
  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Validate all fields - only called on button click
  const validateAll = () => {
    const newErrors = {};
    let hasErrors = false;

    Object.keys(rulesRef.current).forEach((field) => {
      const value = values[field];
      const rule = rulesRef.current[field];
      const error = rule(value, values);

      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }

      // Mark all fields as touched
      setTouched((prev) => ({ ...prev, [field]: true }));
    });

    setErrors(newErrors);
    setIsValid(!hasErrors);
    return !hasErrors;
  };

  // Reset form
  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsValid(false);
  };

  // Set specific error
  const setError = (field, message) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
    setIsValid(false);
  };

  // Clear specific error
  const clearError = (field) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    // Recalculate validity
    setIsValid((prev) => {
      const remaining = Object.keys(errors).filter((key) => key !== field);
      return remaining.length === 0;
    });
  };

  // Get field props for input elements
  const getFieldProps = (field) => ({
    value: values[field],
    onChange: (e) => handleChange(field, e.target.value),
    onBlur: () => handleBlur(field),
    // The following keys should not be spread on DOM nodes directly
    error: touched[field] ? errors[field] : null,
    isTouched: touched[field],
    hasError: touched[field] && !!errors[field],
  });

  return {
    values,
    errors,
    touched,
    isValid,
    handleChange,
    handleBlur,
    validateAll,
    resetForm,
    setError,
    clearError,
    getFieldProps,
    setValues,
  };
};

// Common validation rules
export const validationRules = {
  email: (value) => {
    if (!value) return "Please check your email or password";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please check your email or password";
    return "";
  },

  password: (value) => {
    if (!value) return "Please check your email or password";
    if (value.length < 6) return "Please check your email or password";
    return "";
  },

  required: (value, fieldName = "This field") => {
    if (!value || value.trim() === "") return `${fieldName} is required`;
    return "";
  },

  minLength:
    (min) =>
    (value, fieldName = "This field") => {
      if (!value) return `${fieldName} is required`;
      if (value.length < min)
        return `${fieldName} must be at least ${min} characters`;
      return "";
    },

  maxLength:
    (max) =>
    (value, fieldName = "This field") => {
      if (!value) return "";
      if (value.length > max)
        return `${fieldName} must be no more than ${max} characters`;
      return "";
    },
};
